import React from "react";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import { useForm, Controller } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { AdminLoginParams, LoginResponse } from "@lib/types/login";
import { loginAdmin } from "@lib/api/mutations/auth";
import { Role, useAuth } from "@context/AuthContext";
import { useNavigate } from "react-router-dom";
import { AxiosError } from "axios";
import useHandleError from "@lib/utils/errorHook";

const AdminLogin: React.FC = () => {
  const { login } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleError = useHandleError();

  const [error, setError] = React.useState<string>("");

  // Define Zod schema for validation
  const schema = z.object({
    email: z.string().email("Please enter a valid email."),
    password: z.string().min(6, "Password must be at least 6 characters."),
  });

  // Initialize react-hook-form with Zod validation
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<AdminLoginParams>({
    resolver: zodResolver(schema),
  });

  const {
    mutate: loginMutation,
    isError: isLoginError,
    isPending: isLoginLoading,
  } = useMutation({
    mutationFn: (data: AdminLoginParams) => loginAdmin(data),
    onSuccess: (data: LoginResponse) => {
      login(
        Role.Admin,
        data.token,
        `${data.info.first_name} ${data.info.last_name}`
      );

      navigate("/admin/dashboard");
    },
    onError: (error: AxiosError | unknown) => {
      if (error instanceof AxiosError && error.response) {
        const errorDetails = handleError(error.response.data);
        errorDetails && setError(errorDetails.message);
      } else {
        setError(t("general.error"));
      }
    },
  });

  const onSubmit = (data: AdminLoginParams) => {
    loginMutation(data);
  };

  return (
    <div
      className="flex h-screen items-center justify-center bg-cover bg-center bg-no-repeat"
      style={{
        backgroundImage: `url(/assets/bg.jpg)`,
      }}
    >
      <div className="w-full max-w-sm rounded-lg bg-white p-8 shadow-lg ">
        <Typography variant="h4" className="mb-6 text-center text-gray-800 ">
          {t("admin.login.header")}
        </Typography>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col gap-4"
          noValidate
        >
          {/* Email Field */}
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={t("admin.login.email")}
                type="email"
                fullWidth
                error={!!errors.email}
                helperText={errors.email?.message}
                variant="outlined"
              />
            )}
          />
          {/* Password Field */}
          <Controller
            name="password"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={t("admin.login.password")}
                type="password"
                fullWidth
                error={!!errors.password}
                helperText={errors.password?.message}
                variant="outlined"
              />
            )}
          />
          {/* Error Message */}
          {error && (
            <Typography variant="body1" className="text-red-500 text-center">
              {error}
            </Typography>
          )}

          {/* Login Button */}
          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            size="large"
            disabled={isLoginLoading}
          >
            {isLoginLoading
              ? t("admin.login.loggingIn")
              : t("admin.login.login")}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default AdminLogin;
